/**
 * Batch-8 rapportage — reistijd-afwijking-signaal: DTO's + Zod-schemas voor de
 * actie-endpoints (lijst, "Standaard aanpassen", "Niet aanpassen") en de
 * instelbare drempels.
 */

import { z } from 'zod'

export const REISTIJD_SIGNAAL_STATUS = [
  'OPEN',
  'AFGEHANDELD',
  'AFGEWEZEN',
  'VERLOPEN',
] as const
export type ReistijdSignaalStatusKey = (typeof REISTIJD_SIGNAAL_STATUS)[number]

export type ReistijdSignaalRichting = 'TE_LANG' | 'TE_KORT'

/** Eén signaal in de lijst/het actiescherm. */
export interface ReistijdSignaalDTO {
  id: string
  medewerkerId: string
  medewerker: string
  clientId: string | null
  klant: string
  /** 'HEEN' (woon→klant) of 'TERUG' (klant→woon). */
  richtingLeg: 'HEEN' | 'TERUG'
  /** TE_LANG = structureel langer dan de standaard, TE_KORT = korter. */
  afwijkingRichting: ReistijdSignaalRichting
  /** Huidige standaard-reistijd (DistanceCache) in minuten. */
  standaardMinuten: number
  /** Voorstel: mediaan werkelijke reistijd, afgerond op hele minuten. */
  voorstelMinuten: number
  /** Mediaan-afwijking (werkelijk − berekend) in minuten, +/−. */
  medianAfwijkingMinuten: number
  aantalRitten: number
  /** "YYYY-MM-DD". */
  vensterVan: string
  vensterTot: string
  status: ReistijdSignaalStatusKey
  createdAt: string
}

export const reistijdSignalenQuerySchema = z.object({
  status: z.enum(REISTIJD_SIGNAAL_STATUS).optional().default('OPEN'),
})
export type ReistijdSignalenQuery = z.infer<typeof reistijdSignalenQuerySchema>

/** "Niet aanpassen": optionele reden; het signaal wordt afgewezen + onderdrukt. */
export const reistijdSignaalAfwijzenSchema = z.object({
  reden: z.string().max(500).optional(),
})
export type ReistijdSignaalAfwijzenInput = z.infer<typeof reistijdSignaalAfwijzenSchema>

// ─── Instelbare drempels ─────────────────────────────────────────────────────

export interface ReistijdSignaalInstellingenDTO {
  minRitten: number
  minMediaanMinuten: number
  consistentiePercentage: number
  onderdrukkingDagen: number
}

export const reistijdSignaalInstellingenSchema = z.object({
  minRitten: z.number().int().min(1).max(100),
  minMediaanMinuten: z.number().int().min(1).max(600),
  consistentiePercentage: z.number().int().min(1).max(100),
  onderdrukkingDagen: z.number().int().min(1).max(365),
})
export type ReistijdSignaalInstellingenInput = z.infer<
  typeof reistijdSignaalInstellingenSchema
>
